import { Title } from '@solidjs/meta'
import { type Component, createSignal, Show } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import UpdateBanner from '~/components/UpdateBanner'

const UpdateBannerPage: Component = () => {
  const [show, setShow] = createSignal(false)

  return (
    <>
      <Title>UpdateBanner - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">UpdateBanner</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Aviso fijo de nueva versión disponible. Pensado para PWAs: se muestra cuando el service worker detecta una actualización (<code class="text-primary">needRefresh</code>) y dispara la
            recarga al pulsar el botón.
          </p>
        </div>

        <div class="space-y-12">
          {/* Demo en vivo */}
          <Card>
            <Card.Header
              title="Demo en vivo"
              subtitle="Lanza el banner real (aparece fijo en la parte inferior de la ventana)."
              icon={<Icon icon="material-symbols-light:refresh" class="text-primary" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col items-start gap-4 p-8 bg-surface-muted rounded-2xl border border-border border-dashed">
                <p class="text-foreground-muted">Pulsa el botón para simular que hay una nueva versión disponible.</p>
                <Button color="primary" startIcon="material-symbols-light:refresh" disabled={show()} onClick={() => setShow(true)}>
                  Simular actualización
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Vista previa estática (réplica del aspecto) */}
          <Card>
            <Card.Header title="Anatomía" subtitle="Aspecto del banner: icono, mensaje y acción." icon={<Icon icon="material-symbols-light:visibility-outline" class="text-info" size={24} />} />
            <Card.Body>
              <div class="rounded-2xl overflow-hidden border border-border">
                <div class="bg-slate-900 border-t border-slate-700 px-4 md:px-8 py-3 flex items-center gap-3 flex-wrap sm:flex-nowrap">
                  <div class="shrink-0 h-9 w-9 rounded-full bg-primary-500/15 flex items-center justify-center">
                    <Icon icon="material-symbols-light:refresh" size={20} class="text-primary-400" />
                  </div>
                  <p class="flex-1 text-sm text-slate-200 min-w-0">Hay una nueva versión disponible.</p>
                  <Button variant="contained" color="primary" size="sm" startIcon="material-symbols-light:refresh">
                    Actualizar
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Propiedades del componente UpdateBanner." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Default</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary">onUpdate</td>
                      <td class="px-6 py-4 italic text-xs">() =&gt; void</td>
                      <td class="px-6 py-4 font-mono">requerido</td>
                      <td class="px-6 py-4 italic">Acción al pulsar "Actualizar" (normalmente recarga el SW).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">message</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">'Hay una nueva versión…'</td>
                      <td class="px-6 py-4">Texto del aviso.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary">updateLabel</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">'Actualizar'</td>
                      <td class="px-6 py-4 italic">Texto del botón de acción.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>

      <Show when={show()}>
        <UpdateBanner message="Hay una nueva versión disponible (demo)." onUpdate={() => setShow(false)} />
      </Show>
    </>
  )
}

export default UpdateBannerPage
